/**
 * DataQualityHealthTab.js — Admin Settings tab that runs the server-side
 * data-quality sweep and renders a scorecard.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, RefreshCw, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/admin`;

const FIELD_LABELS = {
  "customer_data.pan": "PAN Number",
  "customer_data.pan_number": "PAN Number (alt)",
  "customer_data.gstin": "GSTIN",
  "customer_data.nationality": "Nationality",
  "customer_data.country": "Country",
  "customer_data.email": "Email",
  "customer_data.phone": "Phone",
  "customer_data.date_of_birth": "Date of Birth",
  "status_risk_consistency": "Status ↔ Risk Consistency",
  "kyc_math_invariant": "KYC Math (Run = V+F+P)",
};

export function DataQualityHealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/data-quality-report`, { withCredentials: true });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to run data-quality report";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); }, []);

  const score = data?.summary?.data_quality_score ?? 0;
  const scoreColor = score >= 95 ? "#10b981" : score >= 80 ? "#f59e0b" : "#ef4444";

  return (
    <div data-testid="data-quality-tab" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShieldCheck style={{ width: 20, height: 20, color: "#10b981" }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Data Quality Health</h3>
          <span style={{
            background: "rgba(37,99,235,0.15)", border: "1px solid #2563eb55",
            borderRadius: 4, padding: "2px 8px", fontSize: 9, fontWeight: 700,
            color: "#60a5fa", textTransform: "uppercase", letterSpacing: 0.5,
          }}>Admin Only</span>
        </div>
        <button onClick={run} disabled={loading} data-testid="rerun-dq"
          style={{ background: "transparent", border: "1px solid #1e2530",
                   color: "#94a3b8", borderRadius: 6, padding: "6px 12px",
                   fontSize: 11, fontWeight: 600, cursor: "pointer",
                   display: "inline-flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={11} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Re-run Sweep
        </button>
      </div>

      <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
        Server-side validation sweep across all customer records: PAN/GSTIN/email/phone/DOB/ISO-3166 country,
        plus cross-field consistency (status↔risk_level) and KYC math invariant (Run = Verified+Failed+Pending).
      </p>

      {data && (
        <>
          {/* Score */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-5">
            <div style={{ background: "#0d1117", border: `1px solid ${scoreColor}55`, borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Data Quality Score</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: scoreColor, lineHeight: 1.1 }}>{score}%</div>
            </div>
            <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Records Scanned</div>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{data.customer_count}</div>
            </div>
            <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Fields Checked</div>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{data.summary.total_checked}</div>
            </div>
            <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 10, color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>Violations</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: data.summary.total_violations ? "#ef4444" : "#10b981" }}>
                {data.summary.total_violations}
              </div>
            </div>
          </div>

          {/* Per-field breakdown */}
          <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 8 }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #1e2530",
                          fontSize: 10, fontWeight: 700, color: "#475569",
                          textTransform: "uppercase", letterSpacing: 1.2 }}>
              Per-Field Breakdown
            </div>
            {data.checks.map(c => {
              const label = FIELD_LABELS[c.field] || c.field;
              const ok = c.violations === 0;
              return (
                <div key={c.field} data-testid={`dq-row-${c.field}`}
                  style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 3fr",
                           gap: 12, padding: "10px 14px", borderBottom: "1px solid #0f1520",
                           alignItems: "center", fontSize: 12 }}>
                  <div className="flex items-center gap-2">
                    {ok
                      ? <CheckCircle size={13} style={{ color: "#10b981" }} />
                      : <AlertTriangle size={13} style={{ color: "#f59e0b" }} />}
                    <span style={{ color: "#cbd5e1", fontWeight: 600 }}>{label}</span>
                  </div>
                  <span style={{ color: "#94a3b8", fontFamily: "ui-monospace, monospace" }}>{c.total_checked} checked</span>
                  <span style={{ color: c.violations ? "#ef4444" : "#475569", fontFamily: "ui-monospace, monospace", fontWeight: 700 }}>
                    {c.violations} bad
                  </span>
                  <span style={{ color: c.violation_rate > 0 ? "#f59e0b" : "#475569" }}>
                    {c.violation_rate}%
                  </span>
                  <span style={{ color: "#64748b", fontSize: 10, fontStyle: "italic" }}>
                    {c.examples?.[0]?.reason || c.examples?.[0]?.msg || (ok ? "—" : "see audit log")}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 12, fontSize: 10, color: "#475569" }}>
            Last run: {new Date(data.ran_at).toLocaleString()}
          </div>
        </>
      )}

      {error && !data && (
        <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid #dc262655",
                      borderRadius: 6, padding: 12, color: "#fca5a5", fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
}

export default DataQualityHealthTab;
